import axios, { type AxiosRequestConfig } from 'axios';
import { debug, error } from '@/utils/logger';
import type { MetricDefinitionsPayload } from '@/utils/metricDefinitions';
import User from '@/models/user';
import { setAuthorFollowing } from '@/lib/authorFollowStore';

const API_URL = process.env.NEXT_PUBLIC_APP_API_BASE_URL;

const FOLLOW_CREATOR_URL = `${API_URL}/api/v2/user/follow`;
const UNFOLLOW_CREATOR_URL = `${API_URL}/api/v2/user/unfollow`;
const FOLLOWERS_URL = (userId: string) => `${API_URL}/api/v1/user/followers/${userId}`;
const FOLLOWING_URL = (userId: string) => `${API_URL}/api/v1/user/following/${userId}`;
const CREATOR_STATS_URL = (userId: string) => `${API_URL}/api/v2/creators/${userId}/stats`;
const ADMIN_CREATOR_STATS_URL = `${API_URL}/api/v2/admin/creators/stats`;
const ADMIN_CREATOR_IMPRESSIONS_URL = `${API_URL}/api/v2/admin/creators/impressions`;
const ADMIN_CREATOR_CONTENT_VIEW_BREAKDOWN_URL = (userId: string) =>
  `${API_URL}/api/v2/admin/creators/${userId}/content-views/breakdown`;
const ADMIN_POSTS_CONTENT_VIEWS_URL = `${API_URL}/api/v2/admin/posts/content-views`;

const getAuthHeaders = () => {
  const rawAccessToken = typeof window !== 'undefined' ? localStorage.getItem('accessToken') : null;

  if (!rawAccessToken) {
    error("[creators:getAuthHeaders] No access token found in localStorage");
    throw new Error('Authentication token not found. User must be logged in.');
  }

  const accessToken = rawAccessToken.startsWith('Bearer ')
    ? rawAccessToken.substring(7)
    : rawAccessToken;

  const headers: Record<string, string> = {
    'Authorization': accessToken
  };

  const rawIdToken = typeof window !== 'undefined' ? localStorage.getItem('idToken') : null;
  if (rawIdToken) {
    const idToken = rawIdToken.startsWith('Bearer ') ? rawIdToken.substring(7) : rawIdToken;
    headers['x-id-token'] = idToken;
  }

  return headers;
};

export interface CreatorEarningsBreakdown {
  self_booking_confirmed: number;
  self_booking_pending: number;
  travel_advisor_confirmed: number;
  travel_advisor_pending: number;
  /** GetYourGuide experience commission (confirmed). */
  experiences_confirmed?: number;
  /** GetYourGuide experience commission (pending). */
  experiences_pending?: number;
  /** Stay22 affiliate commission (confirmed). */
  stay22_confirmed?: number;
  /** Stay22 affiliate commission (pending). */
  stay22_pending?: number;
  lifetime_total_confirmed: number;
}

export interface CreatorStats {
  user_id: string;
  link_views: number;
  signups_driven: number;
  access_count_total: number;
  /**
   * Legacy aggregate: published posts with `user_id` only (see backend
   * `CREATOR_STATS_CONTENT_VIEWS_FIELDS.md`). Do not use for the profile /
   * modal “Content views” headline unless you intentionally want that definition.
   */
  total_post_views: number;
  /**
   * Sum of `view_count` for owner-library posts (non-draft, not hidden, excludes
   * Question/Reply; matches non-draft breakdown scope). Present on current API;
   * optional for older cached responses.
   */
  total_post_views_owner_library?: number;
  total_recommendation_views: number;
  total_profile_views: number;
  booking_clicks_driven?: number;
  total_bookings_driven?: number;
  /** Nuitee hotel bookings only (subset of total_bookings_driven). */
  hotel_bookings?: number;
  /** GetYourGuide experience bookings only (subset of total_bookings_driven). */
  experience_bookings?: number;
  /** Stay22 affiliate stay bookings only (subset of total_bookings_driven). */
  stay22_bookings?: number;
  total_booking_value?: number;
  total_earned?: number;
  total_pending?: number;
  /**
   * GYG widget reach from Partner Portal Campaigns import.
   * Not date-filterable; distinct from in-app Views.
   */
  experience_visits?: number;
  /** Channel split for Earnings Breakdown (hotel self-booking, travel advisor, lifetime). */
  earnings_breakdown?: CreatorEarningsBreakdown;
  total_saves_received?: number;
  total_remixes?: number;
  /**
   * Confirmed view_events where this creator is referral_creator_id (any content).
   * Date-filterable via from/to. Prefer over scope=link headline sum when present.
   */
  total_referred_views?: number;
  /** Total audience size for the "Trusted by N travelers" profile header stat. */
  total_audience?: number;
  /** Canonical labels/definitions from backend metric_definitions_service. */
  metric_definitions?: MetricDefinitionsPayload;
}

export interface GetCreatorStatsParams {
  from?: string;
  to?: string;
  scope?: 'all' | 'link';
  /**
   * When true, a 401/403 from this read won't trip the global axios auth
   * interceptor's force-logout. Stats are non-critical, so a forbidden/expired
   * response should surface as an in-page error rather than ejecting the user
   * from the whole app (see the Performance Dashboard logout bug).
   */
  skipAuthErrorHandling?: boolean;
}

/**
 * Total for the “Content views” headline (profile Views stat + modal tile):
 * owner-library post views + all recommendation views (`exclude_auto=false` scope
 * on recommendations totals). Matches `GET /api/v2/creators/:userId/stats` intent
 * for breakdown alignment per backend docs.
 *
 * If `total_post_views_owner_library` is missing (older API), falls back to
 * `total_post_views + total_recommendation_views`.
 */
export function getContentViewsHeadlineTotal(stats: CreatorStats): number {
  const rec = stats.total_recommendation_views ?? 0;
  if (typeof stats.total_post_views_owner_library === 'number') {
    return stats.total_post_views_owner_library + rec;
  }
  return (stats.total_post_views ?? 0) + rec;
}

/**
 * Share-attributed views for the link performance summary card.
 * Prefers `total_referred_views` (referral_creator_id-only count from view_events).
 * Falls back to scope=link headline sum on older API responses.
 */
export function getShareAttributedViewsTotal(stats: CreatorStats): number {
  if (typeof stats.total_referred_views === 'number') {
    return stats.total_referred_views;
  }
  return getContentViewsHeadlineTotal(stats);
}

/**
 * Admin diagnostic: token opens that did not also increment link_views.
 * Invariant: access_count_total >= link_views per creator; negative diff is a data anomaly.
 */
export function getOpensWithoutLinkViews(
  accessCountTotal: number | null | undefined,
  linkViews: number | null | undefined,
): number | null {
  if (accessCountTotal == null || linkViews == null) return null;
  const diff = accessCountTotal - linkViews;
  if (diff < 0) return null;
  return diff;
}

export interface AdminCreatorStatsEntry {
  user_id: string;
  name: string;
  small_avatar_url: string | null;
  large_avatar_url: string | null;
  link_views: number;
  signups_driven: number;
  access_count_total: number;
  total_post_views: number;
  total_recommendation_views: number;
  total_profile_views: number;
  profile_type?: string | null;
}

export interface AdminCreatorStatsResponse {
  creators: AdminCreatorStatsEntry[];
  total: number;
  limit: number;
  skip: number;
}

export interface AdminCreatorStatsParams {
  limit?: number;
  skip?: number;
  sort_by?: 'link_views' | 'signups_driven' | 'access_count_total'
           | 'total_post_views' | 'total_recommendation_views' | 'total_profile_views';
  sort_dir?: 'asc' | 'desc';
}

/** One creator row from GET /api/v2/admin/creators/impressions */
export interface AdminCreatorImpressionsEntry {
  user_id: string;
  name: string;
  small_avatar_url: string | null;
  total_post_views: number;
  total_recommendation_views: number;
  total_content_views: number;
  total_profile_views: number;
}

export type AdminCreatorLinkStats = Pick<
  AdminCreatorStatsEntry,
  'link_views' | 'signups_driven' | 'access_count_total'
>;

/** Reach admin row total: content views + profile views. */
export function getAdminReachViewsTotal(creator: AdminCreatorImpressionsEntry): number {
  return creator.total_content_views + creator.total_profile_views;
}

export interface AdminCreatorImpressionsResponse {
  creators: AdminCreatorImpressionsEntry[];
  total: number;
  limit: number;
  skip: number;
}

export interface AdminCreatorImpressionsParams {
  limit?: number;
  skip?: number;
  sort_by?: 'total_post_views' | 'total_recommendation_views' | 'total_content_views' | 'total_profile_views';
  sort_dir?: 'asc' | 'desc';
}

/** One item row from GET /api/v2/admin/creators/:userId/content-views/breakdown */
export interface AdminContentViewBreakdownItem {
  resource_id: string;
  resource_type: 'post' | 'recommendation';
  name: string | null;
  post_type: string | null;
  view_count: number;
  status?: string;
  is_draft?: boolean;
  is_hidden?: boolean;
  is_private?: boolean;
}

export interface AdminContentViewBreakdownResponse {
  items: AdminContentViewBreakdownItem[];
  total: number;
  limit: number;
  skip: number;
}

export interface AdminContentViewBreakdownParams {
  limit?: number;
  skip?: number;
  sort_dir?: 'asc' | 'desc';
}

/** One post row from GET /api/v2/admin/posts/content-views */
export interface AdminPostContentViewsEntry {
  post_id: string;
  name: string | null;
  post_type: string | null;
  view_count: number;
  status?: string;
  is_draft?: boolean;
  is_hidden?: boolean;
  is_private?: boolean;
  /** Post owner display name when the API includes it. */
  owner_name?: string | null;
  /** Alternate key some responses use for the owner’s display name. */
  user_name?: string | null;
  owner_user_id?: string | null;
  /** Author / owner user id on the post document when the API returns it. */
  user_id?: string | null;
  /** Populated owner user from a lookup, when the API returns it. */
  user?: { name?: string | null; _id?: string | null } | null;
}

export interface AdminPostContentViewsResponse {
  posts: AdminPostContentViewsEntry[];
  total: number;
  limit: number;
  skip: number;
}

export interface AdminPostContentViewsParams {
  limit?: number;
  skip?: number;
  sort_dir?: 'asc' | 'desc';
}

export interface FollowResponse {
    success?: boolean;
    message?: string;
    [key: string]: unknown;
}

/**
 * Get attribution stats for a creator.
 * Authenticated users can fetch their own stats; admins can fetch any creator's.
 * @param userId - The creator's user ID
 */
export async function getCreatorStats(
  userId: string,
  params?: GetCreatorStatsParams,
): Promise<CreatorStats> {
  try {
    const headers = getAuthHeaders();
    const config: AxiosRequestConfig & { skipAuthErrorHandling?: boolean } = {
      headers,
      params: {
        ...(params?.from ? { from: params.from } : {}),
        ...(params?.to ? { to: params.to } : {}),
        ...(params?.scope ? { scope: params.scope } : {}),
      },
    };
    if (params?.skipAuthErrorHandling) {
      config.skipAuthErrorHandling = true;
    }
    const response = await axios.get<CreatorStats>(CREATOR_STATS_URL(userId), config);
    return response.data;
  } catch (err) {
    error('[getCreatorStats] Error fetching creator stats:', err);
    throw err;
  }
}

/**
 * Get aggregated creator stats for the admin leaderboard.
 * Admin only — non-admins get 403.
 */
export async function getAdminCreatorStats(
  params: AdminCreatorStatsParams = {}
): Promise<AdminCreatorStatsResponse> {
  try {
    const headers = getAuthHeaders();
    const response = await axios.get<AdminCreatorStatsResponse>(ADMIN_CREATOR_STATS_URL, {
      headers,
      params: {
        limit: params.limit ?? 50,
        skip: params.skip ?? 0,
        sort_by: params.sort_by ?? 'link_views',
        sort_dir: params.sort_dir ?? 'desc',
      },
    });
    return response.data;
  } catch (err) {
    error('[getAdminCreatorStats] Error fetching admin creator stats:', err);
    throw err;
  }
}

/**
 * Get creator impressions leaderboard for the admin Page Impressions tab.
 * Admin only — non-admins get 403.
 * Only creators with at least one impression appear.
 * Uses owner-library scope for total_post_views (aligned with breakdown endpoint).
 */
export async function getAdminCreatorImpressions(
  params: AdminCreatorImpressionsParams = {}
): Promise<AdminCreatorImpressionsResponse> {
  try {
    const headers = getAuthHeaders();
    const response = await axios.get<AdminCreatorImpressionsResponse>(
      ADMIN_CREATOR_IMPRESSIONS_URL,
      {
        headers,
        params: {
          limit: params.limit ?? 50,
          skip: params.skip ?? 0,
          sort_by: params.sort_by ?? 'total_content_views',
          sort_dir: params.sort_dir ?? 'desc',
        },
      }
    );
    return response.data;
  } catch (err) {
    error('[getAdminCreatorImpressions] Error fetching creator impressions:', err);
    throw err;
  }
}

/**
 * Get per-item content view breakdown for a single creator.
 * Admin only — non-admins get 403.
 * Returns posts (owner-library scope) and recommendations merged and sorted.
 */
export async function getAdminCreatorContentViewBreakdown(
  userId: string,
  params: AdminContentViewBreakdownParams = {}
): Promise<AdminContentViewBreakdownResponse> {
  try {
    const headers = getAuthHeaders();
    const response = await axios.get<AdminContentViewBreakdownResponse>(
      ADMIN_CREATOR_CONTENT_VIEW_BREAKDOWN_URL(userId),
      {
        headers,
        params: {
          limit: params.limit ?? 50,
          skip: params.skip ?? 0,
          sort_dir: params.sort_dir ?? 'desc',
        },
      }
    );
    return response.data;
  } catch (err) {
    error('[getAdminCreatorContentViewBreakdown] Error fetching content view breakdown:', err);
    throw err;
  }
}

/**
 * Get all posts sorted by view_count for the admin Page Impressions "All posts" view.
 * Admin only — non-admins get 403.
 */
export async function getAdminPostContentViews(
  params: AdminPostContentViewsParams = {}
): Promise<AdminPostContentViewsResponse> {
  try {
    const headers = getAuthHeaders();
    const response = await axios.get<AdminPostContentViewsResponse>(
      ADMIN_POSTS_CONTENT_VIEWS_URL,
      {
        headers,
        params: {
          limit: params.limit ?? 50,
          skip: params.skip ?? 0,
          sort_dir: params.sort_dir ?? 'desc',
        },
      }
    );
    return response.data;
  } catch (err) {
    error('[getAdminPostContentViews] Error fetching posts by view count:', err);
    throw err;
  }
}

export async function followCreator(followUserId: string): Promise<FollowResponse> {
    try {
        debug('Following creator with data:', FOLLOW_CREATOR_URL, { followUserId });
        const response = await axios.post(`${FOLLOW_CREATOR_URL}`, { followUserId });
        debug('Response:', response.data);
        setAuthorFollowing(followUserId, true);
        return response.data;
    } catch (err) {
        error('Error following creator:', err);
        throw err;
    }
}

export async function unfollowCreator(unfollowUserId: string): Promise<FollowResponse> {
    try {
        debug('Unfollowing creator with data:', UNFOLLOW_CREATOR_URL, { unfollowUserId });
        const response = await axios.post(`${UNFOLLOW_CREATOR_URL}`, { unfollowUserId });
        debug('Response:', response.data);
        setAuthorFollowing(unfollowUserId, false);
        return response.data;
    } catch (err) {
        error('Error unfollowing creator:', err);
        throw err;
    }
}

/**
 * Get list of users following a specific user
 * @param userId - The user ID to get followers for
 * @returns Array of User objects who are following the user
 */
export async function getFollowers(userId: string): Promise<User[]> {
    try {
        if (!userId) {
            debug('[getFollowers] No userId provided, returning empty array');
            return [];
        }

        const headers = getAuthHeaders();
        debug('[getFollowers] Fetching followers for user:', { userId });
        
        const response = await axios.get(FOLLOWERS_URL(userId), { headers });
        
        const followers = Array.isArray(response.data)
            ? response.data.map((item: unknown) => new User(item as Record<string, unknown>))
            : [];
        
        debug('[getFollowers] Successfully fetched followers', {
            userId,
            count: followers.length
        });
        
        return followers;
    } catch (err) {
        // Handle 404 gracefully (user not found)
        if (axios.isAxiosError(err) && err.response?.status === 404) {
            debug('[getFollowers] User not found (404), returning empty array', { userId });
            return [];
        }
        
        error('[getFollowers] Error fetching followers:', err);
        throw err;
    }
}

/**
 * Get list of users that a specific user is following
 * @param userId - The user ID to get following list for
 * @returns Array of User objects that the user is following
 */
export async function getFollowing(userId: string): Promise<User[]> {
    try {
        if (!userId) {
            debug('[getFollowing] No userId provided, returning empty array');
            return [];
        }

        const headers = getAuthHeaders();
        debug('[getFollowing] Fetching following for user:', { userId });
        
        const response = await axios.get(FOLLOWING_URL(userId), { headers });
        
        const following = Array.isArray(response.data)
            ? response.data.map((item: unknown) => new User(item as Record<string, unknown>))
            : [];
        
        debug('[getFollowing] Successfully fetched following', {
            userId,
            count: following.length
        });
        
        return following;
    } catch (err) {
        // Handle 404 gracefully (user not found)
        if (axios.isAxiosError(err) && err.response?.status === 404) {
            debug('[getFollowing] User not found (404), returning empty array', { userId });
            return [];
        }
        
        error('[getFollowing] Error fetching following:', err);
        throw err;
    }
}